import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Collapse,
    Switch,
    FormControlLabel,
    useTheme,
    alpha,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button
} from '@mui/material';
import FallRescueAnimation from './components/FallRescueAnimation';
import {
    ExpandLess,
    ExpandMore,
    Animation as AnimationIcon
} from '@mui/icons-material';
import { autoJsStoreUtils, autoWebViewJs } from '@droid/android';
import { authService } from '@src/services/authService';
import FallDetectionSettings from './FallDetectionSettings';
import { VoiceEmergencySettingsRef } from './components/VoiceEmergencySettings';
import { SENSOR_MONITORING_STATE_CHANGED } from './constants';
import { PrivacyDialogDisplay } from '@src/components/privacies/PrivacyDialogDisplay';
import { fileMapUtils, IDispatcher } from '@fanfanlo';
import { privacyDialogClosedEventType } from '@src/components/privacies/PrivacyDialogAuto';
import { FallDownVideoPlayer } from './components/FallDownVideoPlayer';
import { useAutoStartPermission } from './hooks/useAutoStartPermission';

interface FallDetectionWrapperProps {
    title: string;
    isExpanded: boolean;
    onToggleExpanded: (expanded: boolean) => void;
    animationType?: 'fall' | 'shake';
    dispatcher?:IDispatcher
}
interface IPrivacyInfo {
  agree: boolean;
  disagreeTime: number;
}
interface IWaitPlayVideoInfo{
    isAppFirstOpen:boolean;
}
// 配置选项
const REQUIRE_LOGIN_FOR_FALL_DETECTION = true; // 默认不启用登录检测

const FallDetectionWrapper: React.FC<FallDetectionWrapperProps> = ({
    title,
    isExpanded,
    onToggleExpanded,
    animationType,
    dispatcher
}) => {
    const theme = useTheme();
    // 复制自 SensorMonitor.tsx - 状态管理
    const [fallThreshold, setFallThreshold] = useState<string>('5.0');
    const voiceSettingsRef = useRef<VoiceEmergencySettingsRef>(null);
    
    // 监听展开状态变化，展开时重新加载VoiceEmergencySettings配置
    useEffect(() => {
        if (isExpanded && voiceSettingsRef.current) {
            voiceSettingsRef.current.reloadConfig();
        }
    }, [isExpanded]);
    
    const [fallIsMonitoring, setFallIsMonitoring] = useState<boolean>(false);
    const [fallSensorInfoDialogOpen, setFallSensorInfoDialogOpen] = useState(false);
    const [fallForegroundServiceDialogOpen, setFallForegroundServiceDialogOpen] = useState(false);
    
    // 隐私协议相关状态
    const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);

    // 通知权限相关状态
    const [notificationPermissionDialogOpen, setNotificationPermissionDialogOpen] = useState(false);
    const notificationPermissionCallbackRef = useRef<(passed: boolean) => void>();

    // 自启动权限引导Hook
    const { checkAutoStartPermission, AutoStartDialog } = useAutoStartPermission();

    // 复制自 SensorMonitor.tsx - useRef
    const fallSensorCallbackRef = useRef<(passed: boolean) => void>();
    const fallForegroundServiceCallbackRef = useRef<(passed: boolean) => void>();
    const privacyCallbackRef = useRef<(passed: boolean) => void>();

    // 复制自 SensorMonitor.tsx - 初始化阈值
    useEffect(() => {
        try {
            const script = `
            var thresholdValue = com.fanfanlo.emergencycall.manager.SensorManager.getThreshold();
            thresholdValue;`;
            const { javaResultString } = autoWebViewJs.callScript(script);
            const thresholdValue = parseFloat(javaResultString || '5.0');
            setFallThreshold(thresholdValue.toString());
        } catch (error) {
            console.error('Error fetching initial threshold value:', error);
        }
    }, []);

    // 复制自 SensorMonitor.tsx - 读取监控状态
    function readFallIsMonitoring() {
        try {
            const script = `
            var isMonitoring = com.fanfanlo.emergencycall.manager.SensorManager.isMonitoring();
            isMonitoring;`;
            const { javaResultString } = autoWebViewJs.callScript(script);
            const isMonitoring = javaResultString === "true";
            // setFallIsMonitoring(isMonitoring);
            // 只在状态真正变化时才更新state
            setFallIsMonitoring(prev => {
                if (prev !== isMonitoring) {
                    return isMonitoring;
                }
                return prev;
            });
        } catch (error) {
            console.error('Error fetching initial monitoring state:', error);
        }
    }

    // 复制自 SensorMonitor.tsx - 初始化监控状态
    useEffect(() => {
        readFallIsMonitoring();
        
        // 监听Android端发送的监控状态变化事件
        const handleMonitoringStateChange = (event: any) => {
            const { isMonitoring: newState } = event.detail || {};
            if (typeof newState === 'boolean') {
                setFallIsMonitoring(newState);
            }
        };

        window.addEventListener(SENSOR_MONITORING_STATE_CHANGED, handleMonitoringStateChange);
        return () => window.removeEventListener(SENSOR_MONITORING_STATE_CHANGED, handleMonitoringStateChange);
    }, []);

    // 复制自 SensorMonitor.tsx - 权限检查函数
    function checkShowFallSensorInfo(callback: (passed: boolean) => void) {
        interface Info {
            passed: boolean;
        }
        const info = autoJsStoreUtils.read<Info>('showSensorInfo', { passed: false });
        if (!info || !info.passed) {
            fallSensorCallbackRef.current = callback;
            setFallSensorInfoDialogOpen(true);
        } else {
            callback(true);
        }
    }

    function checkShowFallInfo(callback: (passed: boolean) => void) {
        checkShowFallSensorInfo(passed => {
            if (!passed) {
                callback(false);
                return;
            }
            checkShowFallForegroundServiceInfo(callback);
        })
    }

    function checkShowFallForegroundServiceInfo(callback: (passed: boolean) => void) {
        interface Info {
            passed: boolean;
        }
        const info = autoJsStoreUtils.read<Info>('showForegroundServiceInfo', { passed: false });
        if (!info || !info.passed) {
            fallForegroundServiceCallbackRef.current = callback;
            setFallForegroundServiceDialogOpen(true);
        } else {
            callback(true);
        }
    }

    // 复制自 SensorMonitor.tsx - 对话框处理函数
    const handleFallSensorDialog = (passed: boolean) => {
        autoJsStoreUtils.write('showSensorInfo', { passed });
        setFallSensorInfoDialogOpen(false);
        if (fallSensorCallbackRef.current) {
            fallSensorCallbackRef.current(passed);
        }
    };

    const handleFallForegroundServiceDialog = (passed: boolean) => {
        autoJsStoreUtils.write('showForegroundServiceInfo', { passed });
        setFallForegroundServiceDialogOpen(false);
        if (fallForegroundServiceCallbackRef.current) {
            fallForegroundServiceCallbackRef.current(passed);
        }
    };

    // 检查隐私协议是否已同意
    function checkPrivacyAgreement(callback: (passed: boolean) => void): boolean {
        interface IPrivacyInfo {
            agree: boolean;
            disagreeTime: number;
        }
        
        const def: IPrivacyInfo = { agree: false, disagreeTime: 0 };
        const res = autoJsStoreUtils.read<IPrivacyInfo>(`privacy_main`, def);
        const value: IPrivacyInfo = res && 'agree' in res ? res : def;
        
        // 已经同意了
        if (value.agree) {
            callback(true);
            return true;
        }
        
        // 需要弹出隐私协议（去掉冷却期逻辑）
        privacyCallbackRef.current = callback;
        setPrivacyDialogOpen(true);
        return false;
    }

    // 处理隐私协议弹窗关闭
    const handlePrivacyDialogClose = (agreed?: boolean) => {
        setPrivacyDialogOpen(false);
        if (privacyCallbackRef.current) {
            privacyCallbackRef.current(agreed === true);
        }
    };

    // 检查通知权限
    function checkNotificationPermission(callback: (passed: boolean) => void) {
        console.log('[跌倒检测] 检查通知权限');
        try {
            // 先检查当前通知权限状态
            const checkScript = `
                var result = com.fanfanlo.emergencycall.manager.PermissionJSInterface.checkNotificationPermission();
                result;
            `;
            const { javaResultString } = autoWebViewJs.callScript(checkScript);
            console.log('[跌倒检测] 通知权限检查结果:', javaResultString);

            if (javaResultString) {
                const checkResult = JSON.parse(javaResultString);
                if (checkResult.error) {
                    console.error('[跌倒检测] 检查通知权限出错:', checkResult.error);
                    callback(true); // 出错时默认通过
                    return;
                }

                if (checkResult.granted) {
                    console.log('[跌倒检测] 通知权限已授权');
                    callback(true);
                    return;
                }

                // 通知权限未授权
                console.log('[跌倒检测] 通知权限未授权, canRequestInApp:', checkResult.canRequestInApp);

                if (checkResult.canRequestInApp) {
                    // Android 13+: 直接请求权限（会弹出系统对话框）
                    console.log('[跌倒检测] Android 13+, 直接请求通知权限');
                    const requestScript = `
                        com.fanfanlo.emergencycall.manager.PermissionJSInterface.requestNotificationPermission();
                    `;
                    autoWebViewJs.callScript(requestScript);
                    // 请求后回调false，让用户再次点击开关（因为无法同步获取权限请求结果）
                    callback(false);
                } else {
                    // Android 12及以下: 显示引导对话框
                    console.log('[跌倒检测] Android 12及以下, 显示引导对话框');
                    notificationPermissionCallbackRef.current = callback;
                    setNotificationPermissionDialogOpen(true);
                }
            } else {
                console.log('[跌倒检测] 获取通知权限状态返回空，跳过检查');
                callback(true);
            }
        } catch (error) {
            console.error('[跌倒检测] 检查通知权限失败:', error);
            callback(true); // 出错时默认通过
        }
    }

    // 处理通知权限对话框（Android 12及以下使用）
    const handleNotificationPermissionDialog = (goToSettings: boolean) => {
        setNotificationPermissionDialogOpen(false);
        if (goToSettings) {
            console.log('[跌倒检测] 用户选择去设置页面开启通知权限');
            try {
                const script = `com.fanfanlo.emergencycall.manager.PermissionJSInterface.openPermissionSettings("notification");`;
                autoWebViewJs.callScript(script);
            } catch (error) {
                console.error('[跌倒检测] 打开通知权限设置页面失败:', error);
            }
        }
        // 无论用户选择什么，都回调false，让用户手动再次点击开关
        if (notificationPermissionCallbackRef.current) {
            notificationPermissionCallbackRef.current(false);
        }
    };

    // 复制自 SensorMonitor.tsx - 主要的开关处理函数
    async function handleToggleFallMonitoring(enabled: boolean) {
        console.log('[跌倒检测] 处理开关状态变更，新状态:', enabled ? '开启' : '关闭');
        
        if (enabled) {
            console.log('[跌倒检测] 开始启用跌倒检测流程');
            
            // 1. 先检查隐私协议
            console.log('[跌倒检测] 步骤1: 检查隐私协议');
            checkPrivacyAgreement(passed => {
                console.log('[跌倒检测] 隐私协议检查回调，用户是否同意:', passed ? '是' : '否');
                if (!passed) {
                    console.log('[跌倒检测] 用户未同意隐私协议，终止流程');
                    return;
                }
                
                // 2. 条件性检查登录状态
                console.log('[跌倒检测] 步骤2: 检查登录状态');
                if (REQUIRE_LOGIN_FOR_FALL_DETECTION) {
                    console.log('[跌倒检测] 需要登录验证，显示登录对话框');
                    authService.showLoginDialog().then(appLoggedIn => {
                        console.log('[跌倒检测] 登录对话框返回结果:', appLoggedIn ? '登录成功' : '登录取消');
                        if (!appLoggedIn) {
                            console.warn('[跌倒检测] 用户未登录，显示警告');
                            alert("未登录无法使用该功能");
                            return;
                        }
                        
                        // 3. 检查权限，然后启动
                        console.log('[跌倒检测] 步骤3: 检查跌倒检测权限');
                        checkShowFallInfo(permissionPassed => {
                            console.log('[跌倒检测] 权限检查结果:', permissionPassed ? '已授权' : '未授权');
                            if (!permissionPassed) {
                                console.log('[跌倒检测] 用户未授权，终止启动');
                                return;
                            }

                            // 4. 检查是否需要显示自启动引导
                            console.log('[跌倒检测] 步骤4: 检查自启动引导');
                            checkAutoStartPermission(() => {
                                // 5. 检查通知权限
                                console.log('[跌倒检测] 步骤5: 检查通知权限');
                                checkNotificationPermission(notificationPassed => {
                                    console.log('[跌倒检测] 通知权限检查结果:', notificationPassed ? '已授权' : '未授权');
                                    if (!notificationPassed) {
                                        console.log('[跌倒检测] 通知权限未授权，终止启动');
                                        return;
                                    }
                                    try {
                                        console.log('[跌倒检测] 正在调用原生方法启动跌倒检测');
                                        const script = `com.fanfanlo.emergencycall.manager.SensorManager.startMonitoring();`;
                                        autoWebViewJs.callScript(script);
                                        console.log('[跌倒检测] 跌倒检测已成功启动');
                                    } catch (error) {
                                        console.error('[跌倒检测] 启动跌倒检测失败:', error);
                                        alert('开始监控失败。');
                                    }
                                });
                            });
                        });
                    });
                } else {
                    // 跳过登录检测，直接进入权限检测
                    console.log('[跌倒检测] 跳过登录检查，直接进行权限检查');
                    checkShowFallInfo(permissionPassed => {
                        console.log('[跌倒检测] 权限检查结果(无登录):', permissionPassed ? '已授权' : '未授权');
                        if (!permissionPassed) {
                            console.log('[跌倒检测] 用户未授权(无登录)，终止启动');
                            return;
                        }

                        // 检查是否需要显示自启动引导
                        console.log('[跌倒检测] 步骤3: 检查自启动引导(无登录)');
                        checkAutoStartPermission(() => {
                            // 4. 检查通知权限
                            console.log('[跌倒检测] 步骤4: 检查通知权限(无登录)');
                            checkNotificationPermission(notificationPassed => {
                                console.log('[跌倒检测] 通知权限检查结果(无登录):', notificationPassed ? '已授权' : '未授权');
                                if (!notificationPassed) {
                                    console.log('[跌倒检测] 通知权限未授权(无登录)，终止启动');
                                    return;
                                }
                                try {
                                    console.log('[跌倒检测] 正在调用原生方法启动跌倒检测(无登录)');
                                    const script = `com.fanfanlo.emergencycall.manager.SensorManager.startMonitoring();`;
                                    autoWebViewJs.callScript(script);
                                    console.log('[跌倒检测] 跌倒检测已成功启动(无登录)');
                                } catch (error) {
                                    console.error('[跌倒检测] 启动跌倒检测失败(无登录):', error);
                                    alert('开始监控失败。');
                                }
                            });
                        });
                    });
                }
            });
        } else {
            // 直接停止，不进行检查
            console.log('[跌倒检测] 正在停止跌倒检测');
            try {
                const script = `com.fanfanlo.emergencycall.manager.SensorManager.stopMonitoring();`;
                console.log('[跌倒检测] 正在调用原生方法停止跌倒检测');
                await autoWebViewJs.callScript(script);
                console.log('[跌倒检测] 跌倒检测已成功停止');
            } catch (error) {
                console.error('[跌倒检测] 停止跌倒检测失败:', error);
                alert('停止监控失败。');
            }
        }
    }

    // 复制自 SensorMonitor.tsx - 定时检查监控状态
    useEffect(() => {
        const id = setInterval(() => {
            readFallIsMonitoring();
        }, 3000); // 每3秒检查一次监控状态
        return () => clearInterval(id);
    }, []);

    // 动画占位组件
    const AnimationPlaceholder: React.FC<{ type?: 'fall' | 'shake' }> = ({ type }) => {
        const getAnimationContent = () => {
            switch (type) {
                case 'fall':
                    return {
                        icon: '🚶‍♂️',
                        title: '摔倒检测',
                        description: '检测到意外跌倒时自动触发紧急呼救',
                        color: theme.palette.error.main
                    };
                case 'shake':
                    return {
                        icon: '📱',
                        title: '摇一摇报警',
                        description: '快速摇动手机触发紧急呼救',
                        color: theme.palette.warning.main
                    };
                default:
                    return {
                        icon: '🚨',
                        title: '紧急呼救',
                        description: '紧急情况下的自动求助功能',
                        color: theme.palette.primary.main
                    };
            }
        };

        const content = getAnimationContent();

        return (
            <Box sx={{ 
                textAlign: 'center',
                color: 'text.secondary',
                px: 4
            }}>
                <Box sx={{ 
                    fontSize: 72, 
                    mb: 2,
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.1)' },
                        '100%': { transform: 'scale(1)' }
                    }
                }}>
                    {content.icon}
                </Box>
                <Typography 
                    variant="h5" 
                    sx={{ 
                        mb: 1, 
                        fontWeight: 600,
                        color: content.color
                    }}
                >
                    {content.title}
                </Typography>
                <Typography 
                    variant="body1" 
                    sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.6
                    }}
                >
                    {content.description}
                </Typography>
                <Box sx={{
                    mt: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    color: 'text.disabled'
                }}>
                    <AnimationIcon sx={{ fontSize: 20 }} />
                    <Typography variant="caption">
                        点击展开设置
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Paper 
            sx={{ 
                mb: 2, 
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:hover': {
                    boxShadow: theme.shadows[4]
                }
            }}
        >
            {/* 标题栏 */}
            <Box 
                sx={{ 
                    p: 2, 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                    }
                }}
            >
                {/* 左侧：标题和开关 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            fontWeight: 600,
                            color: theme.palette.primary.main
                        }}
                    >
                        {title}
                    </Typography>
                    
                    {/* 启用/禁用开关 */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={fallIsMonitoring}
                                onChange={(e) => handleToggleFallMonitoring(e.target.checked)}
                                size="small"
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body2" color="text.secondary">
                                {fallIsMonitoring ? '已启用' : '未启用'}
                            </Typography>
                        }
                        onClick={(e) => e.stopPropagation()}
                        sx={{ margin: 0 }}
                    />
                </Box>

                {/* 右侧：展开/收起按钮 */}
                <IconButton 
                    size="small"
                    onClick={() => onToggleExpanded(!isExpanded)}
                    sx={{ 
                        color: theme.palette.primary.main,
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease-in-out'
                    }}
                >
                    <ExpandMore />
                </IconButton>
            </Box>
            
            {/* 内容区 */}
            <Box>
                {/* 动画区域 - 折叠时显示 */}
                <Collapse in={!isExpanded}>
                    <Box sx={{ 
                        height: 300,
                        width: '100%',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.background.paper, 0.8),
                        borderTop: `1px solid ${theme.palette.divider}`,
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* <FallRescueAnimation type={animationType} /> */}
                        <Box>
                            <FallDownVideoPlayer/>
                        </Box>
                    </Box>
                </Collapse>
                
                {/* 设置区域 - 展开时显示 */}
                <Collapse in={isExpanded}>
                    <Box sx={{ 
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper
                    }}>
                        <FallDetectionSettings voiceSettingsRef={voiceSettingsRef} />
                    </Box>
                </Collapse>
            </Box>

            {/* 复制自 SensorMonitor.tsx - 传感器权限说明对话框 */}
            <Dialog
                open={fallSensorInfoDialogOpen}
                onClose={() => handleFallSensorDialog(false)}
                aria-labelledby="fall-sensor-dialog-title"
                aria-describedby="fall-sensor-dialog-description"
            >
                <DialogTitle id="fall-sensor-dialog-title">
                    {"传感器使用授权"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="fall-sensor-dialog-description">
                        我们需要获取您的身体传感器（如加速度计）权限，以实现跌倒检测功能。这对于在紧急情况下自动触发呼叫至关重要。您是否同意？
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleFallSensorDialog(false)}>不同意</Button>
                    <Button onClick={() => handleFallSensorDialog(true)} autoFocus>
                        同意
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 复制自 SensorMonitor.tsx - 前台服务通知对话框 */}
            <Dialog
                open={fallForegroundServiceDialogOpen}
                onClose={() => handleFallForegroundServiceDialog(false)}
                aria-labelledby="fall-foreground-dialog-title"
                aria-describedby="fall-foreground-dialog-description"
            >
                <DialogTitle id="fall-foreground-dialog-title">
                    {"启用前台服务通知"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="fall-foreground-dialog-description">
                        为了在应用退到后台后仍能持续监控传感器数据，我们需要启用一个前台服务。这会在您的设备通知栏显示一个持续的通知。您是否同意？
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleFallForegroundServiceDialog(false)}>不同意</Button>
                    <Button onClick={() => handleFallForegroundServiceDialog(true)} autoFocus>
                        同意
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 隐私协议弹窗 */}
            <PrivacyDialogDisplay
                open={privacyDialogOpen}
                onClose={handlePrivacyDialogClose}
                type="main"
                area="zh-cn"
            />

            {/* 自启动引导对话框 */}
            <AutoStartDialog />

            {/* 通知权限引导对话框（Android 12及以下使用） */}
            <Dialog
                open={notificationPermissionDialogOpen}
                onClose={() => handleNotificationPermissionDialog(false)}
                aria-labelledby="notification-permission-dialog-title"
                aria-describedby="notification-permission-dialog-description"
            >
                <DialogTitle id="notification-permission-dialog-title">
                    {"需要开启通知权限"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="notification-permission-dialog-description">
                        跌倒检测功能需要通知权限才能正常工作。当检测到跌倒时，系统需要通过通知提醒您和紧急联系人。请前往设置页面开启通知权限。
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleNotificationPermissionDialog(false)}>稍后再说</Button>
                    <Button onClick={() => handleNotificationPermissionDialog(true)} autoFocus>
                        去设置
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default FallDetectionWrapper;